import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskReveal } from "./Reveal";

const RINGS = [
  { size: 620, x: "-14%", y: "34%", opacity: 0.35, speed: 90 },
  { size: 460, x: "-6%", y: "48%", opacity: 0.45, speed: 140 },
  { size: 320, x: "1%", y: "60%", opacity: 0.55, speed: 200 },
  { size: 520, x: "72%", y: "-46%", opacity: 0.22, speed: -70 },
  { size: 360, x: "79%", y: "-30%", opacity: 0.3, speed: -120 },
];

const RingSet = () => {
  const { scrollY } = useScroll();
  return (
    <>
      {RINGS.map((ring, i) => (
        <ParallaxRing key={i} ring={ring} scrollY={scrollY} index={i} />
      ))}
    </>
  );
};

const ParallaxRing = ({ ring, scrollY, index }) => {
  const y = useTransform(scrollY, [0, 700], [0, ring.speed]);
  return (
    <motion.div
      className="hero-ring"
      data-testid={index === 0 ? "hero-iris-rings" : undefined}
      style={{
        width: ring.size,
        height: ring.size,
        left: ring.x,
        top: ring.y,
        opacity: ring.opacity,
        y,
      }}
      initial={{ scale: 0.6, opacity: 0 }}
      animate={{ scale: 1, opacity: ring.opacity }}
      transition={{ duration: 1.8, delay: 0.15 * index, ease: [0.22, 1, 0.36, 1] }}
    />
  );
};

export const Hero = () => {
  const { scrollY } = useScroll();
  const contentY = useTransform(scrollY, [0, 700], [0, 60]);
  const contentOpacity = useTransform(scrollY, [0, 500], [1, 0.35]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#1B3A5C] text-[#FAF7F2]"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.5]"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 20% 100%, rgba(18,41,63,0.9) 0%, transparent 60%)",
        }}
        aria-hidden="true"
      />
      <RingSet />

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 mx-auto max-w-6xl px-6 pb-28 pt-24 sm:pt-32 lg:px-10 lg:pb-40 lg:pt-40"
      >
        <MaskReveal delay={0.15}>
          <p
            data-testid="hero-eyebrow"
            className="text-xs font-semibold uppercase tracking-[0.28em] text-[#C9A66B]"
          >
            Consultant Ophthalmologist
          </p>
        </MaskReveal>

        <h1
          data-testid="hero-name"
          className="font-display mt-6 text-5xl font-light leading-[1.05] tracking-tight text-[#FAF7F2] sm:text-6xl lg:text-7xl"
        >
          <MaskReveal delay={0.3}>Valence</MaskReveal>
          <MaskReveal delay={0.42}>
            <span className="italic text-[#E4D2AA]">A Jordan</span>
          </MaskReveal>
        </h1>

        <MaskReveal delay={0.62}>
          <p
            data-testid="hero-role"
            className="font-display mt-8 max-w-xl text-lg font-light italic leading-relaxed text-[#E4D2AA] sm:text-xl"
          >
            Glaucoma Management &amp; Complex Cataract Surgery
          </p>
        </MaskReveal>

        <MaskReveal delay={0.78}>
          <div
            data-testid="hero-locations"
            className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13.5px] tracking-[0.12em] text-[#FAF7F2]/70"
          >
            <span className="uppercase">Kingston</span>
            <span className="h-1 w-1 rounded-full bg-[#C9A66B]" aria-hidden="true" />
            <span className="uppercase">Ocho Rios</span>
            <span className="h-1 w-1 rounded-full bg-[#C9A66B]" aria-hidden="true" />
            <span className="uppercase">Montego Bay</span>
          </div>
        </MaskReveal>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="mt-16"
        >
          <a
            href="#leaflets"
            data-testid="hero-leaflets-cta"
            className="group inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.22em] text-[#C9A66B] transition-colors duration-300 hover:text-[#E4D2AA]"
          >
            Patient leaflets
            <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[#C9A66B]/40 transition-colors duration-300 group-hover:border-[#E4D2AA]/60">
              <ArrowDown size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </span>
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
};
